import React, { useCallback, useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  RefreshCw,
  Sliders,
  Trash2,
  Zap,
  ShieldAlert,
  Send,
  Settings2,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import GlassCard from "@/components/GlassCard";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/lib/auth";

// Quick-fire usage-target buttons mapped onto a target absolute count. These
// match the spec exactly so the platform owner can flip an account between
// states (under-limit / at-limit / over-limit / Pro near-limit / etc.).
const USAGE_PRESETS = [
  { label: "Reset to 0", target: 0, hint: "Clears month's usage" },
  { label: "Free 4 / 5", target: 4, hint: "Near-limit on Free Trial" },
  { label: "Free 5 / 5", target: 5, hint: "At-limit on Free Trial" },
  { label: "Starter 24 / 25", target: 24, hint: "Near-limit on Starter" },
  { label: "Starter 25 / 25", target: 25, hint: "At-limit on Starter" },
  { label: "Pro 59 / 60", target: 59, hint: "Near-limit on Pro" },
  { label: "Pro 60 / 60", target: 60, hint: "At-limit on Pro" },
];

// AI generation testing presets — separate cap from published posts.
const AI_USAGE_PRESETS = [
  { label: "Reset to 0", target: 0, hint: "Clears month's AI usage" },
  { label: "Free 9 / 10", target: 9, hint: "Near AI limit on Free Trial" },
  { label: "Free 10 / 10", target: 10, hint: "At AI limit on Free Trial" },
  { label: "Starter 49 / 50", target: 49, hint: "Near AI limit on Starter" },
  { label: "Starter 50 / 50", target: 50, hint: "At AI limit on Starter" },
  { label: "Pro 199 / 200", target: 199, hint: "Near AI limit on Pro" },
  { label: "Pro 200 / 200", target: 200, hint: "At AI limit on Pro" },
  { label: "Business 999 / 1000", target: 999, hint: "Near AI limit on Business" },
  { label: "Business 1000 / 1000", target: 1000, hint: "At AI limit on Business" },
];

function Section({ title, description, icon: Icon, action, children, testid }) {
  return (
    <GlassCard className="p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-start gap-3">
          {Icon ? (
            <div className="h-9 w-9 rounded-md bg-gradient-to-br from-[color:var(--accent-warm)] to-[color:var(--brand-secondary)] text-[color:var(--bg-0)] flex items-center justify-center shadow-[0_4px_12px_rgba(255,138,61,0.35)]">
              <Icon className="h-4 w-4" />
            </div>
          ) : null}
          <div>
            <div className="font-display text-base font-semibold">{title}</div>
            {description ? (
              <div className="text-xs text-[color:var(--text-3)] mt-0.5 leading-relaxed max-w-xl">{description}</div>
            ) : null}
          </div>
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
      {children}
    </GlassCard>
  );
}

function EmptyRow({ label = "No items right now.", testid }) {
  return (
    <div className="text-xs text-[color:var(--text-3)] py-3" data-testid={testid}>
      {label}
    </div>
  );
}

export default function AdminOperations() {
  const { user } = useAuth();

  // Usage testing target user (defaults to the admin themselves so testing
  // happens on the owner account — the spec asks for "current user's usage").
  const [targetUserId, setTargetUserId] = useState("");
  const [targetEmail, setTargetEmail] = useState("");
  useEffect(() => {
    if (user?.user_id && !targetUserId) {
      setTargetUserId(user.user_id);
      setTargetEmail(user.email || "");
    }
  }, [user, targetUserId]);

  // Section data
  const [busyPreset, setBusyPreset] = useState(null);
  const [deletionReqs, setDeletionReqs] = useState([]);
  const [syncIssues, setSyncIssues] = useState([]);
  const [usageNear, setUsageNear] = useState([]);
  const [usageAt, setUsageAt] = useState([]);
  const [failedWebhooks, setFailedWebhooks] = useState([]);
  const [failedPublishes, setFailedPublishes] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [dd, sync, near, fw, fp] = await Promise.all([
        api.get("/admin/data-deletion-requests").catch(() => null),
        api.get("/admin/billing/sync-issues").catch(() => null),
        api.get("/admin/usage/near-limit").catch(() => null),
        api.get("/admin/billing/failed-webhooks").catch(() => null),
        api.get("/admin/publishing/failed").catch(() => null),
      ]);
      setDeletionReqs(dd?.data?.requests || []);
      setSyncIssues(sync?.data?.issues || []);
      setUsageNear(near?.data?.near_limit || []);
      setUsageAt(near?.data?.at_limit || []);
      setFailedWebhooks(fw?.data?.events || []);
      setFailedPublishes(fp?.data?.posts || []);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const applyPreset = async (preset) => {
    if (!targetUserId) {
      toast.error("Pick a target user_id first.");
      return;
    }
    setBusyPreset(preset.label);
    try {
      const res = await api.post(`/admin/users/${targetUserId}/set-published-usage`, {
        target_used: preset.target,
      });
      toast.success(
        `Usage set to ${res?.data?.after}/${res?.data?.monthly_limit ?? "∞"} (${res?.data?.effective_plan})`
      );
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set usage");
    } finally {
      setBusyPreset(null);
    }
  };

  // Same shape as `applyPreset` but for the AI generation cap.
  const applyAiPreset = async (preset) => {
    if (!targetUserId) {
      toast.error("Pick a target user_id first.");
      return;
    }
    setBusyPreset("ai-" + preset.label);
    try {
      const res = await api.post(`/admin/users/${targetUserId}/set-ai-generations-usage`, {
        target_used: preset.target,
      });
      toast.success(
        `AI usage set to ${res?.data?.after}/${res?.data?.ai_generations_limit ?? "\u221E"} (${res?.data?.effective_plan})`
      );
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not set AI usage");
    } finally {
      setBusyPreset(null);
    }
  };

  const resetUsage = async () => {
    if (!targetUserId) {
      toast.error("Pick a target user_id first.");
      return;
    }
    setBusyPreset("reset");
    try {
      const res = await api.post(`/admin/users/${targetUserId}/reset-usage`);
      toast.success(
        `Reset: deleted ${res?.data?.generations_deleted} generations, ${res?.data?.publish_records_deleted} publish records.`
      );
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not reset usage");
    } finally {
      setBusyPreset(null);
    }
  };

  const refreshStripeForUser = async (uid) => {
    try {
      const res = await api.post(`/admin/users/${uid}/refresh-stripe`);
      toast.success(`Stripe synced (${res?.data?.plan || "unchanged"} / ${res?.data?.status || ""})`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not refresh from Stripe");
    }
  };

  const resolveDeletion = async (requestId, status) => {
    try {
      await api.post(`/admin/data-deletion-requests/${requestId}/resolve`, {
        status,
        admin_note: null,
      });
      toast.success(`Marked ${status}`);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not resolve");
    }
  };

  return (
    <div className="space-y-6 pb-10" data-testid="admin-operations-page">
      <PageHeader
        title="Operations"
        subtitle="Admin testing tools, deletion requests, and billing/publishing health signals."
      />

      {/* Usage testing tools */}
      <Section
        title="Monthly limit testing tools"
        description="Flip a user's published-post usage to specific targets to exercise near-limit warnings, hard blocks, upgrade prompts, and scheduler pause behavior. Synthetic rows are tagged `admin-testing` so real publishes are never touched."
        icon={Sliders}
        action={
          <Button
            size="sm"
            variant="outline"
            onClick={resetUsage}
            disabled={busyPreset === "reset"}
            className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]"
            data-testid="admin-usage-reset-button"
          >
            {busyPreset === "reset" ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Reset for current period
          </Button>
        }
        testid="admin-usage-tools"
      >
        <div className="grid sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">Target user_id</label>
            <Input
              value={targetUserId}
              onChange={(e) => setTargetUserId(e.target.value.trim())}
              placeholder="user_xxxxxxxx"
              className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
              data-testid="admin-usage-user-input"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.16em] text-[color:var(--text-3)]">Email (label only)</label>
            <Input
              value={targetEmail}
              onChange={(e) => setTargetEmail(e.target.value.trim())}
              placeholder="user@example.com"
              className="mt-1 bg-[color:var(--surface-2)] border-[color:var(--border-subtle)]"
              data-testid="admin-usage-email-input"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          {USAGE_PRESETS.map((p) => (
            <Button
              key={p.label}
              size="sm"
              variant="outline"
              disabled={!!busyPreset}
              onClick={() => applyPreset(p)}
              className="h-auto py-2.5 flex flex-col items-start gap-0.5 border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)] hover:text-[color:var(--text-1)] text-left"
              data-testid={`admin-preset-${p.target}`}
            >
              <span className="font-semibold leading-tight">
                {busyPreset === p.label ? <Loader2 className="h-3 w-3 mr-1 animate-spin inline" /> : null}
                {p.label}
              </span>
              <span className="text-[10px] text-[color:var(--text-3)] font-normal">{p.hint}</span>
            </Button>
          ))}
        </div>
      </Section>

      {/* AI generation usage testing tools */}
      <Section
        title="AI generation testing tools"
        description="Separate cap from published posts \u2014 protects platform AI credits. Flip a user's AI generation count to a specific target to test the at-limit blocker (Generate page shows an Upgrade banner; AI endpoints return 402)."
        icon={Zap}
        testid="admin-ai-usage-tools"
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
          {AI_USAGE_PRESETS.map((p) => (
            <Button
              key={`ai-${p.label}`}
              size="sm"
              variant="outline"
              disabled={!!busyPreset}
              onClick={() => applyAiPreset(p)}
              className="h-auto py-2.5 flex flex-col items-start gap-0.5 border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] hover:bg-[color:var(--surface-3)] hover:text-[color:var(--text-1)] text-left"
              data-testid={`admin-ai-preset-${p.target}`}
            >
              <span className="font-semibold leading-tight">
                {busyPreset === "ai-" + p.label ? <Loader2 className="h-3 w-3 mr-1 animate-spin inline" /> : null}
                {p.label}
              </span>
              <span className="text-[10px] text-[color:var(--text-3)] font-normal">{p.hint}</span>
            </Button>
          ))}
        </div>
      </Section>

      {/* Data Deletion Requests */}
      <Section
        title="Data deletion requests"
        description="Submitted via the public /data-deletion form. Required by Meta App Review."
        icon={Trash2}
        action={
          <Button size="sm" variant="outline" onClick={refresh} className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)]">
            <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Refresh
          </Button>
        }
        testid="admin-deletion-requests"
      >
        {loading ? (
          <EmptyRow label="Loading…" testid="deletion-empty-loading" />
        ) : deletionReqs.length === 0 ? (
          <EmptyRow label="No deletion requests right now." testid="deletion-empty" />
        ) : (
          <div className="space-y-2">
            {deletionReqs.map((req) => (
              <div
                key={req.request_id}
                className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] p-3 flex items-start justify-between gap-3"
                data-testid={`deletion-${req.request_id}`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm">{req.email}</div>
                  <div className="text-[11px] text-[color:var(--text-3)] mt-0.5">
                    {req.status} · {req.created_at?.slice(0, 16).replace("T", " ")} · {req.request_id}
                  </div>
                  {req.note ? <div className="text-xs text-[color:var(--text-2)] mt-1.5">{req.note}</div> : null}
                </div>
                {req.status === "pending" ? (
                  <div className="flex gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => resolveDeletion(req.request_id, "resolved")}
                      className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)] text-[color:var(--bg-0)] h-7 px-2"
                    >
                      <CheckCircle2 className="h-3 w-3 mr-1" />Resolve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resolveDeletion(req.request_id, "rejected")}
                      className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] h-7 px-2"
                    >
                      Reject
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Subscription Sync Issues */}
      <Section
        title="Subscription sync issues"
        description="Subscriptions stuck in incomplete / past_due / pending-webhook state. Use 'Refresh Stripe' to re-pull from Stripe."
        icon={ShieldAlert}
        testid="admin-sync-issues"
      >
        {loading ? (
          <EmptyRow label="Loading…" />
        ) : syncIssues.length === 0 ? (
          <EmptyRow label="No sync issues right now." testid="sync-empty" />
        ) : (
          <div className="space-y-2">
            {syncIssues.map((s) => (
              <div
                key={s.user_id}
                className="rounded-md border border-[color:rgba(255,138,61,0.3)] bg-[color:rgba(255,138,61,0.06)] p-3 flex items-start justify-between gap-3"
                data-testid={`sync-${s.user_id}`}
              >
                <div className="min-w-0">
                  <div className="font-medium text-sm">{s.email || s.user_id}</div>
                  <div className="text-[11px] text-[color:var(--text-3)] mt-0.5 font-mono">
                    status={s.status || "—"} · plan={s.plan || "—"} · sync_pending={String(!!s.stripe_sync_pending)}
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => refreshStripeForUser(s.user_id)}
                  className="border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] text-[color:var(--text-1)] h-7 px-2 shrink-0"
                  data-testid={`sync-refresh-${s.user_id}`}
                >
                  <RefreshCw className="h-3 w-3 mr-1" />Refresh Stripe
                </Button>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Users near/at monthly limit */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Section
          title="Users near monthly limit"
          description=">= 80% of published-post cap this month."
          icon={Zap}
          testid="admin-near-limit"
        >
          {loading ? (
            <EmptyRow label="Loading…" />
          ) : usageNear.length === 0 ? (
            <EmptyRow label="Nobody near their cap." testid="near-empty" />
          ) : (
            <div className="space-y-1.5">
              {usageNear.map((u) => (
                <div
                  key={u.user_id}
                  className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-2)] px-3 py-2 text-xs flex items-center justify-between gap-3"
                  data-testid={`near-${u.user_id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-[color:var(--text-1)]">{u.email || u.user_id}</div>
                    <div className="text-[10px] text-[color:var(--text-3)]">{u.plan} · {u.used}/{u.limit} ({u.percent}%)</div>
                  </div>
                  <div className="w-24 h-1.5 rounded-full bg-[color:var(--surface-3)] overflow-hidden">
                    <div className="h-full bg-[color:var(--accent-warm)]" style={{ width: `${Math.min(100, u.percent)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
        <Section
          title="Users at monthly limit"
          description="100% \u2014 the publish guard blocks new publishes for them."
          icon={ShieldAlert}
          testid="admin-at-limit"
        >
          {loading ? (
            <EmptyRow label="Loading…" />
          ) : usageAt.length === 0 ? (
            <EmptyRow label="Nobody at their cap." testid="at-empty" />
          ) : (
            <div className="space-y-1.5">
              {usageAt.map((u) => (
                <div
                  key={u.user_id}
                  className="rounded-md border border-[color:rgba(255,92,108,0.3)] bg-[color:rgba(255,92,108,0.06)] px-3 py-2 text-xs flex items-center justify-between gap-3"
                  data-testid={`at-${u.user_id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-[color:var(--text-1)]">{u.email || u.user_id}</div>
                    <div className="text-[10px] text-[color:var(--text-3)]">{u.plan} · {u.used}/{u.limit}</div>
                  </div>
                  <span className="text-[color:var(--danger)] font-mono text-[10px]">{u.percent}%</span>
                </div>
              ))}
            </div>
          )}
        </Section>
      </div>

      {/* Failed webhooks */}
      <Section
        title="Failed Stripe webhooks"
        description="Webhooks Stripe sent us that our handler couldn't process. Stripe retries automatically; investigate persistent failures."
        icon={AlertTriangle}
        testid="admin-failed-webhooks"
      >
        {loading ? (
          <EmptyRow label="Loading…" />
        ) : failedWebhooks.length === 0 ? (
          <EmptyRow label="No failed webhooks. Healthy." testid="webhooks-empty" />
        ) : (
          <div className="space-y-1.5">
            {failedWebhooks.map((evt) => (
              <div
                key={evt.event_id}
                className="rounded-md border border-[color:rgba(255,92,108,0.3)] bg-[color:rgba(255,92,108,0.06)] p-3 text-xs"
                data-testid={`webhook-${evt.event_id}`}
              >
                <div className="font-medium">{evt.type}</div>
                <div className="text-[10px] text-[color:var(--text-3)] font-mono">
                  {evt.event_id} · {evt.failed_at?.slice(0, 19).replace("T", " ")}
                </div>
                {evt.error_message ? (
                  <div className="text-[11px] text-[color:var(--danger)] mt-1 leading-relaxed">{evt.error_message}</div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Failed publishes */}
      <Section
        title="Failed publish attempts"
        description="Posts that hit a platform error or the monthly limit. Each row shows the exact reason."
        icon={Send}
        testid="admin-failed-publishes"
      >
        {loading ? (
          <EmptyRow label="Loading…" />
        ) : failedPublishes.length === 0 ? (
          <EmptyRow label="No failed publishes lately." testid="publishes-empty" />
        ) : (
          <div className="space-y-1.5 max-h-96 overflow-y-auto pr-2">
            {failedPublishes.map((p) => (
              <div
                key={p.post_id}
                className="rounded-md border border-[color:rgba(255,92,108,0.3)] bg-[color:rgba(255,92,108,0.06)] p-3 text-xs"
                data-testid={`failed-${p.post_id}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="font-medium">{p.email || p.user_id}</div>
                  <span className="text-[10px] text-[color:var(--text-3)] font-mono">{p.platform} · {p.blocked_by || p.publish_provider || ""}</span>
                </div>
                <div className="text-[11px] text-[color:var(--danger)] mt-1 leading-relaxed">{p.error_message || "—"}</div>
                <div className="text-[10px] text-[color:var(--text-3)] font-mono mt-1">
                  {p.failed_at?.slice(0, 19).replace("T", " ")} · {p.post_id}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
